import React from 'react';
import { Avatar, AvatarGroup, Box, Card, Chip, Tooltip, Typography } from '@mui/material';
import { Business as BusinessIcon, WorkOutline as WorkIcon } from '@mui/icons-material';
import FollowButton from 'components/common/buttons/FollowButton';
import { useUserContext } from 'shared/contexts/UserContext';
import LabActions from './LabActions';
import { markdownToText } from './LabMarkdown';
import { coverBackground, labAvatarSx } from './labTheme';

export default function LabCard({ lab, onNavigate }) {
  const { user } = useUserContext() || {};
  const open = () => onNavigate(`/lab/${lab.id}/`);
  const people = lab.member_people || [];
  const isPI = user?.id && String(user.id) === String(lab.pi);
  return (
    <Card
      onClick={open}
      sx={{
        height: '100%', minWidth: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column',
        transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(0,0,0,0.12)' },
      }}
    >
      <Box sx={{ height: 64, background: coverBackground(lab), position: 'relative' }}>
        {lab.open_positions > 0 && (
          <Chip
            size="small" icon={<WorkIcon sx={{ fontSize: 14 }} />}
            label={`${lab.open_positions} open position${lab.open_positions > 1 ? 's' : ''}`}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.92)', fontSize: '0.7rem', height: 22 }}
          />
        )}
      </Box>
      <Box sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontWeight: 600, fontSize: '1.2rem', lineHeight: 1.3, '&:hover': { textDecoration: 'underline' } }}>
              {lab.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <BusinessIcon sx={{ fontSize: 13 }} /> {lab.organization_detail?.name}
            </Typography>
          </Box>
          <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, mt: 0.25 }}>
            <FollowButton contentType="lab" objectId={lab.id} size="small" variant="outlined" />
          </Box>
        </Box>
        {lab.description && (
          <Typography variant="body2" color="text.secondary" sx={{
            mt: 1, fontSize: '0.8rem', lineHeight: 1.3, overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {markdownToText(lab.description)}
          </Typography>
        )}
        <Box flex={1} />
        {/* Bottom row: lab members (left) + keywords (right), as on consortium cards */}
        {(people.length > 0 || lab.keywords?.length > 0) && (
          <Box mt={1.5} display="flex" justifyContent="space-between" alignItems="flex-end" gap={1.5}>
            {people.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75, fontSize: '0.8rem' }}>Lab Members:</Typography>
                <AvatarGroup
                  max={6} spacing="small" sx={{ width: 'fit-content' }}
                  renderSurplus={(surplus) => `+${surplus}`}
                  componentsProps={{ additionalAvatar: { sx: { width: 32, height: 32, bgcolor: 'transparent', color: 'primary.light', fontSize: '1rem', fontWeight: 500 } } }}
                >
                  {people.map((m) => (
                    <Tooltip key={m.id} title={[m.name, m.lab_title].filter(Boolean).join(' · ')}>
                      <Avatar
                        src={m.avatar_url || undefined}
                        onClick={(e) => { e.stopPropagation(); onNavigate(`/profile/${m.id}`); }}
                        sx={{ width: 32, height: 32, fontSize: '0.85rem', ...labAvatarSx(lab) }}
                      >
                        {(m.name || 'M')[0]}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              </Box>
            )}
            {lab.keywords?.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', flexShrink: 1, minWidth: 0 }}>
                {lab.keywords.slice(0, 4).map((k) => (
                  <Typography key={k} component="span" variant="caption" sx={{
                    bgcolor: 'rgba(99,102,241,0.08)', color: 'text.secondary', px: 0.9, py: 0.3,
                    borderRadius: '6px', lineHeight: 1.6, whiteSpace: 'nowrap', fontSize: '0.68rem',
                  }}>{k}</Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
        {!isPI && <LabActions lab={lab} showFollow={false} sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }} />}
      </Box>
    </Card>
  );
}
